import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import useTranslation from "../../hooks/useTranslation";
import { useGeneratedTextPaginatedList } from "../../hooks/useGeneratedTextPaginatedList";
import { useDailyTextCreate } from "../../hooks/useDailyTextCreate";
import { primeGeneratedTextDetailsQuery } from "../../hooks/useGeneratedTextDetails";
import { useLanguageSpace } from "../../hooks/useLanguageSpace";
import { useLoginStatus } from "../../hooks/useLoginStatus";
import { useUserSettings } from "../../hooks/useUserSettings";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import {
  deriveEffectiveLanguageSpaceScope,
  getAddedLanguageSpacesStorageKey,
} from "../../hooks/useAddedLanguageSpaces";
import { CONTINUE_READING_CTA_BY_LANGUAGE, ENABLED_LANGUAGE_SPACE_IDS, READ_TODAY_CTA_BY_LANGUAGE, TEXT_LANGUAGES } from "../../constants";
import type { GeneratedText } from "../../hooks/types/GeneratedTexts";
import {
  getLocalIsoDate,
  getLocalIsoDateFromTimestamp,
} from "../../utils/getLocalIsoDate";
import { sortGeneratedTexts } from "../../utils/reepriseFeedOrdering";
import { getCsrfCookie } from "../../lib/utils";
import { notifySnackbar } from "../../lib/notifySnackbar";
import type {
  RabbitHoleDiscovery,
  RabbitHoleWeek,
  RabbitHoleTopicSuggestion,
} from "../../hooks/useRabbitHole";
import {
  useRabbitHoleCreateWeek,
  RabbitHoleCreateError,
  useRabbitHoleRefreshTopicSuggestion,
  useRabbitHoleTopicSuggestionsBatch,
} from "../../hooks/useRabbitHole";
import {
  resolveOverviewDailyDisplayTitle,
  selectFeaturedOverviewDailyText,
} from "../../utils/overviewDailyTexts";
import { buildGeneratedTextTitleTranslationRequest } from "../../utils/generatedTextTitleTranslationRequest";
import {
  getResolvedGeneratedTextTitleCacheKey,
  type ResolvedGeneratedTextTitleCache,
} from "../../utils/generatedTextResolvedTitleCache";
import {
  isRabbitHoleDiscoveryRead,
  resolveCanonicalGeneratedTextForRabbitHoleDiscovery,
  resolveEffectiveRabbitHoleDiscovery,
} from "../../utils/rabbitHoleDiscoveryState";
import {
  RabbitHoleCompactEditionHeader,
  RabbitHoleEditionCover,
  RabbitHoleRecommendationCover,
} from "../../components/RabbitHoleEditionCover";
import {
  formatRabbitHoleEditionDateRange,
  getRabbitHoleEditionTitle,
  splitEditionTitle,
} from "../../utils/rabbitHoleEditionDesign";

export const Route = createFileRoute("/app/")({
  component: AppOverviewPage,
});

const OVERVIEW_CHECKLIST_STORAGE_KEY = "app-overview-checklist-v1";
const OVERVIEW_TITLE_TRANSLATION_CACHE_KEY =
  "app-overview-title-translation-cache-v1";
export const OVERVIEW_NATIVE_LANGUAGE_LABELS: Record<string, string> = {
  ENGLISH: "English",
  FRENCH: "Français",
  GERMAN: "Deutsch",
  ITALIAN: "Italiano",
};

function AppOverviewPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { translate, language } = useTranslation();
  const loginStatus = useLoginStatus();
  const userSettings = useUserSettings({
    enabled: loginStatus.data?.state === "logged in",
  });
  const generatedTextList = useGeneratedTextPaginatedList();
  const dailyTextCreate = useDailyTextCreate();
  const { languageSpace, setLanguageSpace } = useLanguageSpace("ENGLISH");
  const currentUserId =
    loginStatus.data?.state === "logged in" ? loginStatus.data.userId : null;
  const addedLanguageSpacesStorageKey =
    getAddedLanguageSpacesStorageKey(currentUserId);
  const [addedLanguageSpaces] = useLocalStorage<string[]>(
    addedLanguageSpacesStorageKey,
    [],
  );
  const [manualChecklistState, setManualChecklistState] = useLocalStorage<
    Record<string, boolean>
  >(
    currentUserId === null
      ? OVERVIEW_CHECKLIST_STORAGE_KEY
      : `${OVERVIEW_CHECKLIST_STORAGE_KEY}:${currentUserId}`,
    {},
  );
  const [titleTranslationCache, setTitleTranslationCache] = useLocalStorage<
    Record<string, string>
  >(
    currentUserId === null
      ? OVERVIEW_TITLE_TRANSLATION_CACHE_KEY
      : `${OVERVIEW_TITLE_TRANSLATION_CACHE_KEY}:${currentUserId}`,
    {},
  );
  const [resolvedTitleCache, setResolvedTitleCache] =
    useLocalStorage<ResolvedGeneratedTextTitleCache>(
      getResolvedGeneratedTextTitleCacheKey(currentUserId),
      {},
    );
  const [openingDailyLanguageId, setOpeningDailyLanguageId] = useState<
    string | null
  >(null);
  const [backgroundDailyTextsByLanguage, setBackgroundDailyTextsByLanguage] =
    useState<Record<string, GeneratedText>>({});
  const [featuredSubtitle, setFeaturedSubtitle] = useState("");
  const backgroundDailyRequestKeysRef = useRef<Record<string, boolean>>({});
  const featuredSubtitleRequestKeyRef = useRef<string | null>(null);

  const serverAddedLanguageSpaces = Array.isArray(
    userSettings.data?.added_language_spaces,
  )
    ? userSettings.data.added_language_spaces
    : [];
  const hasPersistedLocalAddedLanguageSpaces =
    typeof window !== "undefined" &&
    window.localStorage.getItem(addedLanguageSpacesStorageKey) !== null;
  const hasPersistedAddedLanguageSpaces =
    serverAddedLanguageSpaces.length > 0 ||
    hasPersistedLocalAddedLanguageSpaces;
  const effectiveAddedLanguageSpaces =
    serverAddedLanguageSpaces.length > 0
      ? serverAddedLanguageSpaces
      : addedLanguageSpaces;
  const { effectiveLanguageSpaceSet } = deriveEffectiveLanguageSpaceScope({
    addedLanguageSpaces: effectiveAddedLanguageSpaces,
    enabledLanguageSpaceIds: ENABLED_LANGUAGE_SPACE_IDS,
    hasPersistedAddedLanguageSpaces,
    fallbackLanguageSpaceId:
      languageSpace === "ALL" ? "ENGLISH" : languageSpace,
  });
  const languageOptions = TEXT_LANGUAGES.filter((candidate) =>
    effectiveLanguageSpaceSet.has(candidate.id),
  );
  const visibleLanguageOptions =
    languageOptions.length > 0
      ? languageOptions
      : TEXT_LANGUAGES.filter((candidate) =>
          ENABLED_LANGUAGE_SPACE_IDS.includes(
            candidate.id as (typeof ENABLED_LANGUAGE_SPACE_IDS)[number],
          ),
        ).slice(0, 1);
  const rabbitHoleWeekQueries = useQueries({
    queries: visibleLanguageOptions.map((languageOption) => ({
      queryKey: ["rabbit-hole-current", languageOption.id],
      queryFn: async (): Promise<RabbitHoleWeek> => {
        const res = await fetch(
          `/api/reeprise/rabbit-hole-weeks/current/?language=${encodeURIComponent(languageOption.id)}`,
          {
            credentials: "include",
          },
        );
        if (!res.ok) {
          throw new Error("could not fetch rabbit hole week");
        }
        return res.json();
      },
      staleTime: 30_000,
    })),
  });
  const allTexts =
    generatedTextList.data?.pages.flatMap((page) => page.results) ?? [];
  const todayIso = getLocalIsoDate(new Date());
  const rabbitHoleCards = visibleLanguageOptions
    .map((languageOption, index) => ({
      languageOption,
      week: rabbitHoleWeekQueries[index]?.data,
      isLoading: rabbitHoleWeekQueries[index]?.isLoading ?? false,
    }))
    .sort((a, b) => {
      const tierA = getCardSortTier(a.week, todayIso);
      const tierB = getCardSortTier(b.week, todayIso);
      if (tierA !== tierB) return tierA - tierB;
      const completedA = a.week?.completed_discoveries ?? 0;
      const completedB = b.week?.completed_discoveries ?? 0;
      if (completedA !== completedB) return completedB - completedA;
      return 0;
    });
  useEffect(() => {
    const todayRequestKeys: string[] = [];

    for (const languageOption of visibleLanguageOptions) {
      const existingDailyText = findTodayDailyText(
        allTexts,
        languageOption.id,
        todayIso,
      );
      if (existingDailyText) {
        continue;
      }

      const requestKey = `${todayIso}:${languageOption.id}`;
      todayRequestKeys.push(requestKey);

      if (backgroundDailyRequestKeysRef.current[requestKey]) {
        continue;
      }

      backgroundDailyRequestKeysRef.current[requestKey] = true;

      void dailyTextCreate
        .mutateAsync({
          language: languageOption.id,
        })
        .then((created) => {
          setBackgroundDailyTextsByLanguage((prev) => ({
            ...prev,
            [languageOption.id]: created,
          }));
        })
        .catch(() => {
          delete backgroundDailyRequestKeysRef.current[requestKey];
        });
    }

    for (const existingKey of Object.keys(
      backgroundDailyRequestKeysRef.current,
    )) {
      if (!todayRequestKeys.includes(existingKey)) {
        delete backgroundDailyRequestKeysRef.current[existingKey];
      }
    }
  }, [allTexts, dailyTextCreate, todayIso, visibleLanguageOptions]);

  const overviewTasks = visibleLanguageOptions.flatMap((languageOption) =>
    buildOverviewTasks({
      languageOption,
      allTexts,
      backgroundDailyText: backgroundDailyTextsByLanguage[languageOption.id],
      todayIso,
      translate,
      manualChecklistState,
      setManualChecklistState,
      setLanguageSpace,
      navigate,
      queryClient,
      dailyTextCreate,
      openingDailyLanguageId,
      setOpeningDailyLanguageId,
    }),
  );
  const recommendedTasks = overviewTasks.filter(
    (task) => task.section === "recommended",
  );
  const continueTasks = overviewTasks.filter(
    (task) => task.section === "continue",
  );
  const dailyTexts = recommendedTasks
    .map((task) => task.generatedText)
    .filter((text): text is GeneratedText => Boolean(text));
  const dailyTitleEntries = dailyTexts
    .map((text) => {
      const title = resolveOverviewDailyDisplayTitle(text)?.trim() || "";
      return title
        ? {
            textId: text.id,
            sourceLanguage: text.language,
            title,
          }
        : null;
    })
    .filter(
      (
        entry,
      ): entry is {
        textId: number;
        sourceLanguage: string;
        title: string;
      } => Boolean(entry),
    );
  const rabbitHoleTitleEntries = rabbitHoleCards
    .flatMap(
      ({ week }) =>
        week?.discoveries?.map((discovery) => {
          const title = (discovery.topic || "").trim();
          return discovery.id &&
            title &&
            !title.toLowerCase().startsWith("rabbit hole")
            ? {
                textId: discovery.id,
                sourceLanguage: discovery.language,
                title,
              }
            : null;
        }) ?? [],
    )
    .filter(
      (
        entry,
      ): entry is {
        textId: number;
        sourceLanguage: string;
        title: string;
      } => Boolean(entry),
    );
  const resolvedTitleEntries = [
    ...dailyTitleEntries,
    ...rabbitHoleTitleEntries,
  ];
  const featuredDailyText = selectFeaturedOverviewDailyText({
    allTexts,
    dailyTexts,
    languageOrder: visibleLanguageOptions.map((option) => option.id),
  });
  const featuredTask =
    recommendedTasks.find(
      (task) => task.generatedText?.id === featuredDailyText?.id,
    ) ??
    recommendedTasks[0] ??
    null;
  const remainingRecommendedTasks = featuredTask
    ? recommendedTasks.filter((task) => task.id !== featuredTask.id)
    : recommendedTasks;
  const featuredTitle = resolveOverviewDailyDisplayTitle(
    featuredTask?.generatedText,
  );
  const motherTongue = userSettings.data?.mother_tongue ?? "GERMAN";
  const featuredTextId = featuredTask?.generatedText?.id;
  const featuredSourceLanguage = featuredTask?.generatedText?.language;
  const featuredTranslationRequestKey =
    typeof featuredTextId === "number" &&
    featuredTitle?.trim() &&
    featuredSourceLanguage
      ? `${featuredTextId}:${featuredTitle.trim()}:${motherTongue}:${featuredSourceLanguage}`
      : null;
  const featuredCachedSubtitle = featuredTranslationRequestKey
    ? titleTranslationCache[featuredTranslationRequestKey]?.trim() || ""
    : "";

  useEffect(() => {
    if (resolvedTitleEntries.length === 0) {
      return;
    }

    let hasChanged = false;
    const nextCache: ResolvedGeneratedTextTitleCache = {
      ...resolvedTitleCache,
    };
    for (const entry of resolvedTitleEntries) {
      if (nextCache[String(entry.textId)] === entry.title) {
        continue;
      }
      nextCache[String(entry.textId)] = entry.title;
      hasChanged = true;
    }

    if (!hasChanged) {
      return;
    }

    setResolvedTitleCache(nextCache);
  }, [resolvedTitleEntries, resolvedTitleCache, setResolvedTitleCache]);

  useEffect(() => {
    if (
      typeof featuredTextId !== "number" ||
      !featuredSourceLanguage ||
      !featuredTitle?.trim()
    ) {
      featuredSubtitleRequestKeyRef.current = null;
      setFeaturedSubtitle("");
      return;
    }

    const title = featuredTitle.trim();
    if (motherTongue === featuredSourceLanguage) {
      featuredSubtitleRequestKeyRef.current = null;
      setFeaturedSubtitle(title);
      return;
    }

    const requestKey = `${featuredTextId}:${title}:${motherTongue}:${featuredSourceLanguage}`;
    const cachedTranslation = titleTranslationCache[requestKey]?.trim();
    if (cachedTranslation) {
      featuredSubtitleRequestKeyRef.current = requestKey;
      setFeaturedSubtitle(cachedTranslation);
      return;
    }
    if (featuredSubtitleRequestKeyRef.current === requestKey) {
      return;
    }

    featuredSubtitleRequestKeyRef.current = requestKey;
    setFeaturedSubtitle("");

    const abortController = new AbortController();
    void fetch("/api/reeprise/translate/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFTOKEN": getCsrfCookie(),
      },
      credentials: "include",
      signal: abortController.signal,
      body: JSON.stringify(
        buildGeneratedTextTitleTranslationRequest({
          text: title,
          learningLang: motherTongue,
          sourceLang: featuredSourceLanguage,
        }),
      ),
    })
      .then(async (response) => {
        if (!response.ok) {
          return;
        }
        const data = (await response.json()) as { translation?: string };
        const translatedTitle = (data.translation || "").trim();
        if (!translatedTitle) {
          return;
        }
        setTitleTranslationCache((prev) => ({
          ...prev,
          [requestKey]: translatedTitle,
        }));
        setFeaturedSubtitle(translatedTitle);
      })
      .catch(() => {
        // Keep the hero usable even if subtitle translation fails.
      });

    return () => {
      abortController.abort();
    };
  }, [
    featuredSourceLanguage,
    featuredTextId,
    featuredTitle,
    motherTongue,
    setTitleTranslationCache,
    featuredCachedSubtitle,
  ]);

  return (
    <main className="min-h-full bg-white" data-testid="app-overview-page">
      <div className="w-full max-w-none px-4 py-4 pb-24 md:px-3 md:py-8">
        <div className="grid gap-10">
          <div className="space-y-8">
            <RabbitHoleOverviewSection
              cards={rabbitHoleCards}
              navigate={navigate}
              queryClient={queryClient}
              setLanguageSpace={setLanguageSpace}
              todayIso={todayIso}
              appLanguage={language}
              allTexts={allTexts}
            />

            {featuredTask ? (
              <FeaturedOverviewTask
                task={featuredTask}
                title={featuredTitle || featuredTask.label}
                subtitle={
                  featuredSubtitle ||
                  featuredCachedSubtitle ||
                  featuredTitle ||
                  ""
                }
                wordCount={featuredTask.generatedText?.word_number ?? null}
              />
            ) : null}

            <OverviewTaskSections
              recommendedTasks={remainingRecommendedTasks}
              continueTasks={continueTasks}
              compact
              showRecommendedHeading={false}
            />
          </div>
        </div>
      </div>
    </main>
  );
}

function RabbitHoleOverviewSection({
  cards,
  navigate,
  queryClient,
  setLanguageSpace,
  todayIso,
  appLanguage,
  allTexts,
}: {
  cards: Array<{
    languageOption: (typeof TEXT_LANGUAGES)[number];
    week: RabbitHoleWeek | undefined;
    isLoading: boolean;
  }>;
  navigate: ReturnType<typeof useNavigate>;
  queryClient: ReturnType<typeof useQueryClient>;
  setLanguageSpace: (
    value: "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN",
  ) => void;
  todayIso: string;
  appLanguage: string;
  allTexts: GeneratedText[];
}) {
  const { translate } = useTranslation();
  const createWeek = useRabbitHoleCreateWeek();
  const refreshTopicSuggestion = useRabbitHoleRefreshTopicSuggestion();
  const [expandedLanguages, setExpandedLanguages] = useState<
    Record<string, boolean>
  >({});
  const [topicSuggestionOverrides, setTopicSuggestionOverrides] = useState<
    Record<string, RabbitHoleTopicSuggestion>
  >({});
  const [refreshingSuggestionLanguages, setRefreshingSuggestionLanguages] =
    useState<Record<string, boolean>>({});
  const inactiveLanguages = cards
    .filter(({ week }) => !isRabbitHoleCardActive(week))
    .map(({ languageOption }) => languageOption.id);
  const topicSuggestionsQuery =
    useRabbitHoleTopicSuggestionsBatch(inactiveLanguages);

  const openPlanner = async (languageId: string) => {
    setLanguageSpace(languageId as "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN");
    await navigate({
      to: "/app/rabbit_hole",
      search: { weekId: undefined, summary: undefined },
    });
  };

  const toggleExpanded = (languageId: string) => {
    setExpandedLanguages((prev) => ({
      ...prev,
      [languageId]: !prev[languageId],
    }));
  };

  const overviewTopicHeaderLines = Math.max(
    1,
    ...cards.map(({ week }) => {
      const headerTitle = isRabbitHoleCardActive(week)
        ? (week?.topic ?? "")
        : translate("rabbit_hole_next_edition_title");
      return splitEditionTitle(headerTitle, {
        variant: "full",
        maxLines: 3,
        forceEditorialPair: false,
      }).lines.length;
    }),
  );

  const [pickingTopics, setPickingTopics] = useState<Record<string, string>>({});
  const nextEditionHeaderLayout = splitEditionTitle(
    translate("rabbit_hole_topic_suggestion"),
    { variant: "full", maxLines: 3, forceEditorialPair: false },
  );
  useEffect(() => {
    const querySuggestions = topicSuggestionsQuery.data ?? {};
    const mergedSuggestions: Record<string, RabbitHoleTopicSuggestion> = {};
    Object.entries(querySuggestions).forEach(([language, suggestions]) => {
      if (suggestions?.[0]) {
        mergedSuggestions[language] = suggestions[0];
      }
    });
    setTopicSuggestionOverrides((previous) => ({
      ...mergedSuggestions,
      ...previous,
    }));
  }, [topicSuggestionsQuery.data]);

  const allCardsLoading = cards.every(
    (card) => card.isLoading && !card.week,
  );

  return (
    <section data-testid="overview-rabbit-hole-section">
      {allCardsLoading ? (
        <div className="grid min-w-0 gap-3 lg:grid-cols-4" data-testid="overview-rabbit-hole-grid">
          {cards.map(({ languageOption }) => (
            <div
              key={`${languageOption.id}:placeholder`}
              className="w-full min-w-0 animate-pulse rounded-2xl border border-slate-100 bg-slate-50 p-4"
            >
              <div className="h-3 w-16 rounded bg-slate-200" />
              <div className="mt-3 mb-3 space-y-2">
                <div className="h-4 w-full rounded bg-slate-200" />
                <div className="h-4 w-3/4 rounded bg-slate-200" />
              </div>
              <div className="h-2 w-24 rounded bg-slate-200" />
            </div>
          ))}
        </div>
      ) : null}

      {cards.length > 0 ? (
        <div
          className="grid min-w-0 gap-3 lg:grid-cols-4"
          data-testid="overview-rabbit-hole-grid"
        >
          {cards.map(({ languageOption, week, isLoading }) => {
            const isWeekLoading = isLoading && week === undefined;
            const hasWeek = week !== undefined && week.status !== "none";
            const suggestion =
              topicSuggestionOverrides[languageOption.id] ?? null;
            const suggestionLocallyRefreshing =
              refreshingSuggestionLanguages[languageOption.id] === true;
            const suggestionLoading =
              !hasWeek &&
              !isWeekLoading &&
              topicSuggestionsQuery.isLoading &&
              !suggestion;
            const suggestionUnavailable =
              !hasWeek &&
              !isWeekLoading &&
              topicSuggestionsQuery.isSuccess &&
              !suggestion;
            const suggestionRefreshing =
              refreshTopicSuggestion.isPending &&
              refreshTopicSuggestion.variables?.language === languageOption.id;
            const progressLabel =
              hasWeek &&
              typeof week.completed_discoveries === "number" &&
              typeof week.total_discoveries === "number"
                ? translate("rabbit_hole_progress", {
                    completed: week.completed_discoveries,
                    total: week.total_discoveries,
                  })
                : null;
            const todaysDiscovery = findRabbitHoleDiscoveryForDate(
              week,
              getLocalIsoDate(new Date()),
            );
            const effectiveTodaysDiscovery =
              resolveEffectiveRabbitHoleDiscovery({
                discovery: todaysDiscovery,
                generatedTexts: allTexts,
              });
            const todaysEditionTitle = getRabbitHoleEditionTitle(
              todaysDiscovery?.topic,
            );
            const archivedWeek = week as RabbitHoleWeek | undefined;
            const upcomingSlots =
              hasWeek && week
                ? buildAllRabbitHoleSlots(week, todayIso, appLanguage)
                : [];
            const isExpanded = expandedLanguages[languageOption.id] === true;
            const pickTopic = pickingTopics[languageOption.id] ?? "";

            return (
              <div
                key={`${languageOption.id}:${week?.id ?? "unset"}`}
                onClick={async () => {
                  if (todaysDiscovery && effectiveTodaysDiscovery) {
                    void openRabbitHoleDiscovery({
                      discovery: todaysDiscovery,
                      languageSpace: languageOption.id,
                      generatedTexts: allTexts,
                      queryClient,
                      navigate,
                      setLanguageSpace,
                    });
                    return;
                  }
                  if (archivedWeek?.status === "COMPLETED") {
                    void navigate({
                      to: "/app/rabbit_hole",
                      search: { weekId: archivedWeek.id, summary: undefined },
                    });
                  }
                }}
                className={`relative w-full min-w-0 overflow-hidden rounded-2xl bg-white p-4 text-left transition ${
                  todaysDiscovery && effectiveTodaysDiscovery
                    ? "cursor-pointer hover:bg-slate-50"
                    : archivedWeek?.status === "COMPLETED"
                      ? "cursor-pointer hover:bg-slate-50"
                      : ""
                }`}
                data-testid={`overview-rabbit-hole-card-${languageOption.id.toLowerCase()}`}
              >
                {isRabbitHoleCardActive(week) ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      void openPlanner(languageOption.id);
                    }}
                    className="absolute right-4 top-3 z-20 rounded p-1 hover:bg-black/5"
                    data-testid={`overview-rabbit-hole-manage-${languageOption.id.toLowerCase()}`}
                    aria-label={translate("overview_rabbit_hole_manage")}
                  >
                    <EllipsisHorizontalIcon className="h-5 w-5 shrink-0 text-slate-500" />
                  </button>
                ) : null}

                {isRabbitHoleCardActive(week) ? (
                  <RabbitHoleEditionCover
                    topic={week.topic}
                    title={todaysEditionTitle.title}
                    language={languageOption.id}
                    languageLabel={
                      OVERVIEW_NATIVE_LANGUAGE_LABELS[languageOption.id] ||
                      translate(languageOption.prettyTranslationKey)
                    }
                    weekStart={week.week_start}
                    weekEnd={week.week_end}
                    dayIndex={todaysDiscovery?.rabbit_hole_day_index ?? 0}
                    completed={week.completed_discoveries ?? 0}
                    total={week.total_discoveries ?? 6}
                    dayLabel={translate("rabbit_hole_edition_day", {
                      day: (todaysDiscovery?.rabbit_hole_day_index ?? 0) + 1,
                    })}
                    progressLabel={
                      progressLabel ||
                      translate("rabbit_hole_progress", {
                        completed: 0,
                        total: week.total_discoveries ?? 6,
                      })
                    }
                    verticalLabel={translate(
                      "rabbit_hole_edition_vertical_label",
                    )}
                    dateRangeLabel={formatRabbitHoleEditionDateRange(
                      week.week_start,
                      week.week_end,
                      appLanguage,
                    )}
                    isRead={isRabbitHoleDiscoveryRead(effectiveTodaysDiscovery)}
                    titlePending={todaysEditionTitle.pending}
                    unavailableLabel={translate(
                      "overview_rabbit_hole_no_today_title",
                    )}
                    actionLabel={READ_TODAY_CTA_BY_LANGUAGE[languageOption.id] ?? translate("rabbit_hole_read_today")}
                    readActionLabel={CONTINUE_READING_CTA_BY_LANGUAGE[languageOption.id] ?? translate("rabbit_hole_continue_reading")}
                    onAction={
                      todaysDiscovery && effectiveTodaysDiscovery
                        ? () =>
                            void openRabbitHoleDiscovery({
                              discovery: todaysDiscovery,
                              languageSpace: languageOption.id,
                              generatedTexts: allTexts,
                              queryClient,
                              navigate,
                              setLanguageSpace,
                            })
                        : undefined
                    }
                    variant="compact"
                    topicHeaderLines={overviewTopicHeaderLines}
                    testId={`overview-rabbit-hole-edition-${languageOption.id.toLowerCase()}`}
                  />
                ) : archivedWeek?.status === "COMPLETED" ? (
                  <div className="flex flex-col">
                    <RabbitHoleEditionCover
                      topic={archivedWeek.topic}
                      title={translate("rabbit_hole_complete")}
                      language={languageOption.id}
                      languageLabel={
                        OVERVIEW_NATIVE_LANGUAGE_LABELS[languageOption.id] ||
                        translate(languageOption.prettyTranslationKey)
                      }
                      weekStart={archivedWeek.week_start}
                      weekEnd={archivedWeek.week_end}
                      dayIndex={6}
                      completed={archivedWeek.completed_discoveries ?? 0}
                      total={archivedWeek.total_discoveries ?? 6}
                      dayLabel={translate("rabbit_hole_this_week")}
                      progressLabel={translate("rabbit_hole_progress", {
                        completed: archivedWeek.completed_discoveries ?? 0,
                        total: archivedWeek.total_discoveries ?? 6,
                      })}
                      verticalLabel={translate(
                        "rabbit_hole_edition_vertical_label",
                      )}
                      dateRangeLabel={formatRabbitHoleEditionDateRange(
                        archivedWeek.week_start,
                        archivedWeek.week_end,
                        appLanguage,
                      )}
                      unavailableLabel={translate(
                        "overview_rabbit_hole_archived_title",
                      )}
                      actionLabel={translate("rabbit_hole_open_summary")}
                      onAction={() =>
                        void navigate({
                          to: "/app/rabbit_hole",
                          search: { weekId: archivedWeek.id, summary: true },
                        })
                      }
                      variant="compact"
                      topicHeaderLines={overviewTopicHeaderLines}
                      testId={`overview-rabbit-hole-archive-${languageOption.id.toLowerCase()}`}
                    />
                    <p
                      className="mt-3 text-sm text-slate-500"
                      data-testid={`overview-rabbit-hole-archive-label-${languageOption.id.toLowerCase()}`}
                    >
                      {translate("overview_rabbit_hole_archived_title")}
                    </p>
                  </div>
                ) : createWeek.isPending &&
                  createWeek.variables?.language === languageOption.id ? (
                  <div onClick={(e) => e.stopPropagation()}>
                    <RabbitHoleCompactEditionHeader
                      languageLabel={
                        OVERVIEW_NATIVE_LANGUAGE_LABELS[languageOption.id] ||
                        translate(languageOption.prettyTranslationKey)
                      }
                      titleLayout={nextEditionHeaderLayout}
                      headerLines={overviewTopicHeaderLines}
                      testId={`overview-rabbit-hole-recommendation-header-${languageOption.id.toLowerCase()}`}
                    />
                    <div className="mt-5 lg:mx-auto lg:max-w-[18rem]">
                      <RabbitHoleRecommendationCover
                        topic={
                          suggestionLocallyRefreshing ? "" : (suggestion?.topic ?? "")
                        }
                        language={languageOption.id}
                        weekStart={todayIso}
                        verticalLabel={translate(
                          "rabbit_hole_edition_vertical_label",
                        )}
                        recommendationLabel={
                          OVERVIEW_NATIVE_LANGUAGE_LABELS[languageOption.id] ||
                          translate(languageOption.prettyTranslationKey)
                        }
                        variant="compact"
                        titlePending={
                          createWeek.isPending ||
                          suggestionLocallyRefreshing ||
                          suggestionRefreshing ||
                          !suggestion?.topic
                        }
                        testId={`overview-rabbit-hole-recommendation-${languageOption.id.toLowerCase()}`}
                      />
                    </div>
                  </div>
                ) : isWeekLoading ? (
                  <div className="pr-8 animate-pulse">
                    <div className="h-3 w-16 rounded bg-slate-200" />
                    <div className="mt-3 mb-3 space-y-2">
                      <div className="h-4 w-full rounded bg-slate-200" />
                      <div className="h-4 w-3/4 rounded bg-slate-200" />
                    </div>
                    <div className="h-2 w-24 rounded bg-slate-200" />
                  </div>
                ) : (
                  <div onClick={(e) => e.stopPropagation()}>
                    <RabbitHoleCompactEditionHeader
                      languageLabel={
                        OVERVIEW_NATIVE_LANGUAGE_LABELS[languageOption.id] ||
                        translate(languageOption.prettyTranslationKey)
                      }
                      titleLayout={nextEditionHeaderLayout}
                      headerLines={overviewTopicHeaderLines}
                      testId={`overview-rabbit-hole-recommendation-header-${languageOption.id.toLowerCase()}`}
                    />

                    <div className="mt-5 lg:mx-auto lg:max-w-[18rem]">
                      <RabbitHoleRecommendationCover
                        topic={suggestionRefreshing ? "" : (suggestion?.topic ?? "")}
                        language={languageOption.id}
                        weekStart={todayIso}
                        verticalLabel={translate(
                          "rabbit_hole_edition_vertical_label",
                        )}
                        recommendationLabel={
                          OVERVIEW_NATIVE_LANGUAGE_LABELS[languageOption.id] ||
                          translate(languageOption.prettyTranslationKey)
                        }
                        variant="compact"
                        titlePending={suggestionRefreshing || !suggestion?.topic}
                        testId={`overview-rabbit-hole-recommendation-${languageOption.id.toLowerCase()}`}
                      />
                    </div>

                    <div className={`mt-3 lg:mx-auto lg:max-w-[18rem] ${suggestionRefreshing ? "opacity-70" : ""}`}>
                      <div className="flex flex-col gap-2">
                        {suggestion?.topic ? (
                          <button
                            type="button"
                            onClick={() => {
                              void createWeek
                                .mutateAsync({
                                  topic: suggestion.topic,
                                  language: languageOption.id,
                                  topic_source: "IDEA",
                                  topic_suggestion_id: suggestion.id,
                                })
                                .then((createdWeek) => {
                                  setLanguageSpace(
                                    languageOption.id as
                                      | "ENGLISH"
                                      | "FRENCH"
                                      | "GERMAN"
                                      | "ITALIAN",
                                  );
                                  void navigate({
                                    to: "/app/rabbit_hole",
                                    search: {
                                      weekId: createdWeek.id,
                                      summary: undefined,
                                    },
                                  });
                                })
                                .catch((error: unknown) => {
                                  void queryClient.invalidateQueries({
                                    queryKey: ["rabbit-hole-current"],
                                  });
                                  notifySnackbar({
                                    message:
                                      error instanceof RabbitHoleCreateError &&
                                      error.code === "rabbit_hole_active"
                                        ? translate(
                                            "rabbit_hole_active_edition",
                                          )
                                        : translate(
                                            "rabbit_hole_create_error",
                                          ),
                                  });
                                });
                            }}
                            className="inline-flex min-h-12 w-full items-center justify-between rounded-[5px] border border-slate-950 bg-slate-950 px-5 text-left text-sm font-medium text-white transition hover:border-slate-800 hover:bg-slate-800 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-300 disabled:text-slate-500"
                            disabled={suggestionRefreshing || suggestionLoading}
                            data-testid={`overview-rabbit-hole-recommendation-start-${languageOption.id.toLowerCase()}`}
                          >
                            {translate("rabbit_hole_choose_recommended")}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => {
                            setRefreshingSuggestionLanguages((previous) => ({
                              ...previous,
                              [languageOption.id]: true,
                            }));
                            void refreshTopicSuggestion
                              .mutateAsync({
                                language: languageOption.id,
                              })
                              .then((nextSuggestion) => {
                                setTopicSuggestionOverrides((previous) => ({
                                  ...previous,
                                  [languageOption.id]: nextSuggestion,
                                }));
                              })
                              .catch(() => {
                                // Keep the current suggestion visible on refresh failure.
                              })
                              .finally(() => {
                                setRefreshingSuggestionLanguages((previous) => {
                                  const next = { ...previous };
                                  delete next[languageOption.id];
                                  return next;
                                });
                              });
                          }}
                          disabled={suggestionRefreshing || suggestionLoading}
                          className="inline-flex items-center gap-2 self-start text-sm font-medium text-slate-600 transition hover:text-slate-900 disabled:cursor-not-allowed disabled:text-slate-300"
                          data-testid={`overview-rabbit-hole-regenerate-${languageOption.id.toLowerCase()}`}
                        >
                          <ArrowPathIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                          <span>{translate("rabbit_hole_another_recommendation")}</span>
                        </button>
                      </div>
                    </div>

                    {suggestionUnavailable ? (
                      <p className="mt-4 text-sm text-slate-500">
                        {translate("overview_rabbit_hole_no_recommendation")}
                      </p>
                    ) : null}

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const trimmed = pickTopic.trim();
                        if (!trimmed) return;
                        createWeek
                          .mutateAsync({
                            topic: trimmed,
                            language: languageOption.id,
                          })
                          .then((createdWeek) => {
                            setLanguageSpace(languageOption.id as "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN");
                            void navigate({
                              to: "/app/rabbit_hole",
                              search: { weekId: createdWeek.id, summary: undefined },
                            });
                          })
                          .catch((error: unknown) => {
                            void queryClient.invalidateQueries({
                              queryKey: ["rabbit-hole-current"],
                            });
                            notifySnackbar({
                              message:
                                error instanceof RabbitHoleCreateError &&
                                error.code === "rabbit_hole_active"
                                  ? translate("rabbit_hole_active_edition")
                                  : translate("rabbit_hole_create_error"),
                            });
                          });
                      }}
                      className="mt-4 border-t border-slate-200 pt-4 lg:mx-auto lg:max-w-[18rem]"
                    >
                      <div className="flex min-h-12 items-center rounded-[5px] border border-slate-300 bg-white focus-within:border-slate-500">
                        <input
                          type="text"
                          value={pickTopic}
                          onChange={(e) =>
                            setPickingTopics((prev) => ({
                              ...prev,
                              [languageOption.id]: e.target.value,
                            }))
                          }
                          placeholder={translate(
                            "rabbit_hole_custom_topic_placeholder",
                          )}
                          className="min-w-0 flex-1 bg-transparent px-4 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none"
                          data-testid={`overview-rabbit-hole-topic-input-${languageOption.id.toLowerCase()}`}
                        />
                        <button
                          type="submit"
                          disabled={!pickTopic.trim()}
                          className="flex h-11 w-12 shrink-0 items-center justify-center text-slate-700 transition hover:text-slate-950 disabled:text-slate-300"
                          aria-label={translate(
                            "rabbit_hole_custom_topic_submit",
                          )}
                          data-testid={`overview-rabbit-hole-topic-submit-${languageOption.id.toLowerCase()}`}
                        >
                          <ArrowRightIcon
                            className="h-5 w-5"
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {isRabbitHoleCardActive(week) ? (
                  <div className="mt-4 min-w-0 border-t border-slate-100 pt-4 lg:mx-auto lg:max-w-[18rem]">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(languageOption.id);
                      }}
                      className="flex w-full min-w-0 items-center justify-between gap-3 text-left"
                      data-testid={`overview-rabbit-hole-expand-${languageOption.id.toLowerCase()}`}
                    >
                      <span className="min-w-0 break-words text-sm leading-5 text-slate-700">
                        {translate("overview_rabbit_hole_upcoming_title")}
                      </span>
                      <ChevronDownIcon
                        className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {isExpanded ? (
                      <div
                        className="mt-3 grid w-full min-w-0 max-w-full grid-cols-2 gap-3 overflow-hidden sm:grid-cols-3 lg:grid-cols-2"
                        data-testid={`overview-rabbit-hole-expanded-${languageOption.id.toLowerCase()}`}
                      >
                        {upcomingSlots.map((slot) => {
                          const isSummarySlot = "isSummary" in slot && slot.isSummary;
                          const slotEditionTitle = isSummarySlot
                            ? { title: translate("rabbit_hole_open_summary"), pending: false }
                            : getRabbitHoleEditionTitle(slot.discovery?.topic);
                          const effectiveSlotDiscovery = isSummarySlot
                            ? undefined
                            : resolveEffectiveRabbitHoleDiscovery({
                                discovery: slot.discovery,
                                generatedTexts: allTexts,
                              });
                          const canOpen = isSummarySlot
                            ? Boolean(week?.id)
                            : Boolean(
                                slot.discovery &&
                                  (slot.isCompleted ||
                                    slot.isToday ||
                                    slot.isTomorrow),
                              );
                          const summaryProgressLabel = translate(
                            "rabbit_hole_weekly_review",
                          );
                          const summaryDayLabel = translate("rabbit_hole_open_summary");
                          const summaryActionLabel = translate("rabbit_hole_open_summary");
                          return (
                            <RabbitHoleEditionCover
                              key={slot.index}
                              topic={week.topic}
                              title={slotEditionTitle.title}
                              language={languageOption.id}
                              languageLabel={
                                OVERVIEW_NATIVE_LANGUAGE_LABELS[
                                  languageOption.id
                                ] ||
                                translate(languageOption.prettyTranslationKey)
                              }
                              weekStart={week.week_start}
                              weekEnd={week.week_end}
                              dayIndex={slot.index}
                              completed={week.completed_discoveries ?? 0}
                              total={week.total_discoveries ?? 6}
                              dayLabel={
                                isSummarySlot
                                  ? summaryDayLabel
                                  : translate("rabbit_hole_edition_day", {
                                      day: slot.index + 1,
                                    })
                              }
                              progressLabel={
                                isSummarySlot
                                  ? summaryProgressLabel
                                  : slot.isToday
                                    ? `${translate("rabbit_hole_today_label")} · ${slot.dayLabel}`
                                    : slot.isTomorrow
                                      ? `${translate("rabbit_hole_tomorrow_label")} · ${slot.dayLabel}`
                                      : slot.dayLabel
                              }
                              verticalLabel={translate(
                                "rabbit_hole_edition_vertical_label",
                              )}
                              dateRangeLabel={formatRabbitHoleEditionDateRange(
                                week.week_start,
                                week.week_end,
                                appLanguage,
                              )}
                              isRead={isSummarySlot ? false : isRabbitHoleDiscoveryRead(effectiveSlotDiscovery)}
                              titlePending={isSummarySlot ? false : slotEditionTitle.pending}
                              unavailableLabel={
                                isSummarySlot
                                  ? summaryDayLabel
                                  : translate("rabbit_hole_generating")
                              }
                              actionLabel={
                                isSummarySlot
                                  ? summaryActionLabel
                                  : undefined
                              }
                              onAction={
                                isSummarySlot
                                  ? () =>
                                      void navigate({
                                        to: "/app/rabbit_hole",
                                        search: { weekId: week.id as number, summary: true },
                                      })
                                  : canOpen && slot.discovery
                                  ? () =>
                                      void openRabbitHoleDiscovery({
                                        discovery: slot.discovery,
                                        languageSpace: languageOption.id,
                                        generatedTexts: allTexts,
                                        queryClient,
                                        navigate,
                                        setLanguageSpace,
                                      })
                                  : undefined
                              }
                              variant="mini"
                              testId={`overview-rabbit-hole-slot-${languageOption.id.toLowerCase()}-${slot.index}`}
                            />
                          );
                        })}
                        <div className="col-span-2 border-t border-slate-100 pt-3">
                          <p className="text-sm leading-5 text-slate-500">
                            {translate("rabbit_hole_weekly_review")}
                          </p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

function FeaturedOverviewTask({
  task,
  title,
  subtitle,
  wordCount,
}: {
  task: OverviewTask;
  title: string;
  subtitle: string;
  wordCount: number | null;
}) {
  const { translate } = useTranslation();
  const content = (
    <>
      <h1 className="break-words font-serif text-[22px] leading-tight tracking-[0.1px] text-slate-950 hyphens-auto sm:text-[24px] lg:text-[30px]">
        {title}
      </h1>
      <p
        className="mt-1 min-h-[1.4rem] text-[1rem] leading-6 text-slate-600"
        data-testid="overview-featured-daily-subtitle"
      >
        {subtitle}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.9rem] text-slate-500">
        {wordCount ? (
          <span>
            {translate("app_overview_word_count", {
              count: wordCount,
            })}
          </span>
        ) : null}
        <span>{task.detail}</span>
      </div>
    </>
  );

  return (
    <section data-testid="overview-featured-daily">
      {task.navigationTarget ? (
        <Link
          {...task.navigationTarget}
          onClick={task.onBeforeOpen}
          className="block min-w-0 text-left"
          data-testid="overview-featured-daily-open"
        >
          {content}
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => void task.onOpen?.()}
          className="block min-w-0 text-left"
          data-testid="overview-featured-daily-open"
        >
          {content}
        </button>
      )}
    </section>
  );
}

function ChecklistSection({
  title,
  children,
  subdued = false,
}: {
  title: string;
  children: ReactNode;
  subdued?: boolean;
}) {
  return (
    <section>
      <h3
        className={
          subdued
            ? "text-[0.78rem] font-medium tracking-[-0.01em] text-slate-400"
            : "text-[0.82rem] font-medium tracking-[-0.01em] text-slate-500"
        }
      >
        {title}
      </h3>
      <div className="mt-2">{children}</div>
    </section>
  );
}

function OverviewTaskSections({
  recommendedTasks,
  continueTasks,
  compact = false,
  showRecommendedHeading = true,
}: {
  recommendedTasks: OverviewTask[];
  continueTasks: OverviewTask[];
  compact?: boolean;
  showRecommendedHeading?: boolean;
}) {
  const { translate } = useTranslation();

  return (
    <div className={compact ? "space-y-5" : "space-y-8"}>
      {recommendedTasks.length > 0 ? (
        showRecommendedHeading ? (
          <ChecklistSection title={translate("app_overview_recommended")}>
            {recommendedTasks.map((task) => (
              <ChecklistAction key={task.id} {...task} compact={compact} />
            ))}
          </ChecklistSection>
        ) : (
          <section>
            {recommendedTasks.map((task) => (
              <ChecklistAction key={task.id} {...task} compact={compact} />
            ))}
          </section>
        )
      ) : null}

      {continueTasks.length > 0 ? (
        <ChecklistSection
          title={translate("app_overview_continue_label")}
          subdued
        >
          {continueTasks.map((task) => (
            <ChecklistAction key={task.id} {...task} compact={compact} />
          ))}
        </ChecklistSection>
      ) : null}
    </div>
  );
}

type OverviewTask = {
  id: string;
  checked: boolean;
  busy?: boolean;
  onToggle: () => void;
  onOpen?: () => void | Promise<void>;
  onBeforeOpen?: () => void;
  label: string;
  detail: string;
  generatedText?: GeneratedText;
  navigationTarget?: {
    to: "/app/reeprise/$generatedTextId";
    params: {
      generatedTextId: string;
    };
    search: {
      chapter: undefined;
      focusSentence: undefined;
      focusToken: undefined;
      focusWord: undefined;
      jumpTo: undefined;
    };
  };
  testId?: string;
  section: "recommended" | "continue";
  kind: "daily" | "continue";
};

function buildGeneratedTextNavigationTarget(generatedTextId: string) {
  return {
    to: "/app/reeprise/$generatedTextId" as const,
    params: {
      generatedTextId,
    },
    search: {
      chapter: undefined,
      focusSentence: undefined,
      focusToken: undefined,
      focusWord: undefined,
      jumpTo: undefined,
    },
  };
}

function ChecklistAction({
  checked,
  busy,
  compact = false,
  onToggle,
  onOpen,
  onBeforeOpen,
  label,
  detail,
  navigationTarget,
  testId,
}: {
  checked: boolean;
  busy?: boolean;
  compact?: boolean;
  onToggle: () => void;
  onOpen?: () => void | Promise<void>;
  onBeforeOpen?: () => void;
  label: string;
  detail: string;
  navigationTarget?: OverviewTask["navigationTarget"];
  testId?: string;
}) {
  const { translate } = useTranslation();
  const content = (
    <>
      <span className="min-w-0">
        <span
          className={`block font-serif font-normal tracking-[0.1px] ${
            testId?.includes("overview-open-continue")
              ? "text-slate-700"
              : "text-slate-950"
          } ${compact ? "text-[0.98rem] leading-5" : "text-[1.02rem] leading-6"}`}
        >
          {label}
        </span>
        <span
          className={`block ${
            testId?.includes("overview-open-continue")
              ? "text-slate-400"
              : "text-slate-500"
          } ${compact ? "text-[0.82rem] leading-5" : "text-sm leading-6"}`}
        >
          {busy ? "…" : detail}
        </span>
      </span>
      <ChevronRightIcon
        className={`mt-1 h-4 w-4 shrink-0 ${
          testId?.includes("overview-open-continue")
            ? "text-slate-300"
            : "text-slate-400"
        }`}
      />
    </>
  );

  return (
    <div
      className={`flex items-start gap-3 transition ${
        compact ? "py-2.5" : "py-3"
      }`}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-label={
          checked
            ? translate("app_overview_checkbox_checked")
            : translate("app_overview_checkbox_unchecked")
        }
        className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:text-slate-950"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        {checked ? (
          <CheckCircleIcon className="h-5 w-5 text-slate-950" />
        ) : (
          <span className="block h-4 w-4 rounded-full border border-slate-400 bg-transparent" />
        )}
      </button>
      <div className="min-w-0 flex-1">
        {navigationTarget ? (
          <Link
            {...navigationTarget}
            onClick={onBeforeOpen}
            className="flex w-full min-w-0 items-start justify-between gap-3 text-left"
            data-testid={testId ? `${testId}-open` : undefined}
          >
            {content}
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => void onOpen?.()}
            className="flex w-full min-w-0 items-start justify-between gap-3 text-left"
            data-testid={testId ? `${testId}-open` : undefined}
          >
            {content}
          </button>
        )}
      </div>
    </div>
  );
}

function buildOverviewTasks(args: {
  languageOption: (typeof TEXT_LANGUAGES)[number];
  allTexts: GeneratedText[];
  backgroundDailyText?: GeneratedText;
  todayIso: string;
  translate: (
    key: string,
    variables?: Record<string, string | number>,
  ) => string;
  manualChecklistState: Record<string, boolean>;
  setManualChecklistState: (value: Record<string, boolean>) => void;
  setLanguageSpace: (
    value: "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN",
  ) => void;
  navigate: ReturnType<typeof useNavigate>;
  queryClient: ReturnType<typeof useQueryClient>;
  dailyTextCreate: ReturnType<typeof useDailyTextCreate>;
  openingDailyLanguageId: string | null;
  setOpeningDailyLanguageId: (value: string | null) => void;
}): OverviewTask[] {
  const {
    languageOption,
    allTexts,
    backgroundDailyText,
    todayIso,
    translate,
    manualChecklistState,
    setManualChecklistState,
    setLanguageSpace,
    navigate,
    queryClient,
    dailyTextCreate,
    openingDailyLanguageId,
    setOpeningDailyLanguageId,
  } = args;
  const languageLabel =
    OVERVIEW_NATIVE_LANGUAGE_LABELS[languageOption.id] ||
    translate(languageOption.prettyTranslationKey);
  const todayDailyText =
    findTodayDailyText(allTexts, languageOption.id, todayIso) ??
    resolveSameDayDailyText(backgroundDailyText, todayIso, languageOption.id);
  const continueText = findContinueText(allTexts, languageOption.id, todayIso);
  const dailyReadAutomatically = Boolean(
    todayDailyText && todayDailyText.view_count > 0,
  );
  const continueCheckedAutomatically = Boolean(
    continueText && continueText.view_count > 0,
  );
  const readDailyTaskKey = buildChecklistKey({
    day: todayIso,
    languageId: languageOption.id,
    taskId: "read-daily",
  });

  const tasks: OverviewTask[] = [
    {
      id: `daily-${languageOption.id.toLowerCase()}`,
      section: "recommended",
      kind: "daily",
      checked:
        dailyReadAutomatically ||
        manualChecklistState[readDailyTaskKey] === true,
      busy: openingDailyLanguageId === languageOption.id,
      onToggle: () =>
        toggleChecklistValue({
          key: readDailyTaskKey,
          state: manualChecklistState,
          setState: setManualChecklistState,
        }),
      onBeforeOpen: todayDailyText
        ? () => {
            setLanguageSpace(
              languageOption.id as "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN",
            );
            primeGeneratedTextDetailsQuery(queryClient, todayDailyText);
          }
        : undefined,
      navigationTarget: todayDailyText
        ? buildGeneratedTextNavigationTarget(String(todayDailyText.id))
        : undefined,
      onOpen: todayDailyText
        ? undefined
        : async () => {
            setLanguageSpace(
              languageOption.id as "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN",
            );
            setOpeningDailyLanguageId(languageOption.id);
            try {
              const created = await dailyTextCreate.mutateAsync({
                language: languageOption.id,
              });
              primeGeneratedTextDetailsQuery(queryClient, created);
              void navigate(
                buildGeneratedTextNavigationTarget(String(created.id)),
              );
            } finally {
              setOpeningDailyLanguageId(null);
            }
          },
      label: resolveOverviewDailyDisplayTitle(todayDailyText) || "",
      detail: languageLabel,
      generatedText: todayDailyText,
      testId: `overview-open-daily-${languageOption.id.toLowerCase()}`,
    },
  ];

  if (!todayDailyText || !tasks[0].label.trim()) {
    tasks.shift();
  }

  if (continueText) {
    tasks.push({
      id: `continue-${languageOption.id.toLowerCase()}`,
      section: "continue",
      kind: "continue",
      checked: continueCheckedAutomatically,
      onToggle: () => undefined,
      onBeforeOpen: () => {
        setLanguageSpace(
          languageOption.id as "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN",
        );
        primeGeneratedTextDetailsQuery(queryClient, continueText);
      },
      navigationTarget: buildGeneratedTextNavigationTarget(
        String(continueText.id),
      ),
      label:
        resolveOverviewDailyDisplayTitle(continueText) ||
        translate("app_overview_continue_reading_headline"),
      detail: languageLabel,
      generatedText: continueText,
      testId: `overview-open-continue-${languageOption.id.toLowerCase()}`,
    });
  }

  return tasks;
}

function buildChecklistKey({
  day,
  languageId,
  taskId,
}: {
  day: string;
  languageId: string;
  taskId: string;
}) {
  return `${day}:${languageId}:${taskId}`;
}

function toggleChecklistValue({
  key,
  state,
  setState,
}: {
  key: string;
  state: Record<string, boolean>;
  setState: (value: Record<string, boolean>) => void;
}) {
  setState({
    ...state,
    [key]: !state[key],
  });
}

function findTodayDailyText(
  texts: GeneratedText[],
  languageId: string,
  todayIso: string,
) {
  return sortGeneratedTexts(
    texts.filter(
      (text) =>
        text.language === languageId &&
        text.origin === "DAILY_NUDGE" &&
        getLocalIsoDateFromTimestamp(text.created_at) === todayIso,
    ),
  )[0];
}

function resolveSameDayDailyText(
  text: GeneratedText | undefined,
  todayIso: string,
  languageId: string,
) {
  if (!text) {
    return undefined;
  }
  if (text.origin !== "DAILY_NUDGE") {
    return undefined;
  }
  if (text.language !== languageId) {
    return undefined;
  }
  if (getLocalIsoDateFromTimestamp(text.created_at) !== todayIso) {
    return undefined;
  }
  return text;
}

function findContinueText(
  texts: GeneratedText[],
  languageId: string,
  todayIso: string,
) {
  const candidates = sortGeneratedTexts(
    texts.filter((text) => {
      if (text.language !== languageId) {
        return false;
      }
      if (
        text.origin === "DAILY_NUDGE" &&
        getLocalIsoDateFromTimestamp(text.created_at) === todayIso
      ) {
        return false;
      }
      if (text.status !== "COMPLETE") {
        return false;
      }
      return text.view_count > 0 || text.learning_progress > 0;
    }),
  );
  return candidates[0];
}

async function openRabbitHoleDiscovery(arg: {
  discovery: RabbitHoleDiscovery;
  languageSpace: string;
  generatedTexts: GeneratedText[];
  queryClient: ReturnType<typeof useQueryClient>;
  navigate: ReturnType<typeof useNavigate>;
  setLanguageSpace: (languageSpace: "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN") => void;
}) {
  if (openingRabbitHoleDiscoveryIds.has(arg.discovery.id)) {
    return;
  }
  openingRabbitHoleDiscoveryIds.add(arg.discovery.id);
  arg.setLanguageSpace(arg.languageSpace as "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN");
  const canonicalDiscoveryText =
    resolveCanonicalGeneratedTextForRabbitHoleDiscovery({
      discovery: arg.discovery,
      generatedTexts: arg.generatedTexts,
    });

  if (canonicalDiscoveryText) {
    primeGeneratedTextDetailsQuery(arg.queryClient, canonicalDiscoveryText);
  }

  try {
    await arg.navigate({
      to: "/app/reeprise/$generatedTextId",
      params: { generatedTextId: String(arg.discovery.id) },
      search: {
        chapter: undefined,
        focusSentence: undefined,
        focusToken: undefined,
        focusWord: undefined,
        jumpTo: undefined,
      },
    });
  } finally {
    openingRabbitHoleDiscoveryIds.delete(arg.discovery.id);
  }
}

const openingRabbitHoleDiscoveryIds = new Set<number>();

function isRabbitHoleCardActive(week: RabbitHoleWeek | undefined): week is RabbitHoleWeek {
  return Boolean(
    week &&
      (week.status === "PLANNING" || week.status === "ACTIVE"),
  );
}

function getCardSortTier(
  week: RabbitHoleWeek | undefined,
  todayIso: string,
): number {
  if (!week || week.status === "none") return 4;
  if (!isRabbitHoleCardActive(week)) return 3;
  const todaysDiscovery = findRabbitHoleDiscoveryForDate(week, todayIso);
  if (!todaysDiscovery) return 1;
  return todaysDiscovery.view_count > 0 ? 2 : 0;
}

function buildAllRabbitHoleSlots(
  week: RabbitHoleWeek,
  todayIso: string,
  appLanguage: string,
) {
  if (!week.discoveries?.length) {
    return [];
  }

  const dateFormat = new Intl.DateTimeFormat(
    appLanguage === "GERMAN" ? "de-DE" : "en-US",
    { weekday: "long", day: "numeric", month: "long" },
  );

  const slotData = week.discoveries
    .filter((d) => typeof d.rabbit_hole_day_index === "number")
    .map((d) => {
      const slotDate = new Date(`${week.week_start}T00:00:00`);
      slotDate.setDate(slotDate.getDate() + d.rabbit_hole_day_index);
      const iso = getLocalIsoDate(slotDate);
      const isCompleted = d.status === "COMPLETE" && d.has_content === true;
      const isToday = iso === todayIso;
      const isFuture = iso > todayIso;
      const tomorrowIso = getLocalIsoDate(
        new Date(new Date().setDate(new Date().getDate() + 1)),
      );
      const isTomorrow = iso === tomorrowIso;
      return {
        index: d.rabbit_hole_day_index,
        iso,
        dayLabel: dateFormat.format(slotDate),
        title: (d.topic || "").replace(/^rabbit hole – /, "").trim(),
        discovery: d,
        isCompleted,
        isToday,
        isFuture,
        isTomorrow,
      };
    })
    .sort((a, b) => a.index - b.index);

  return [
    ...slotData,
    {
      index: 6,
      iso: week.week_end,
      dayLabel: week.week_end,
      title: "",
      discovery: undefined,
      isCompleted: false,
      isToday: false,
      isFuture: false,
      isTomorrow: false,
      isSummary: true,
    },
  ];
}

function findRabbitHoleDiscoveryForDate(
  week: RabbitHoleWeek | undefined,
  dateIso: string,
): RabbitHoleDiscovery | undefined {
  if (!week?.discoveries?.length || !week.week_start) return undefined;
  const weekStart = new Date(`${week.week_start}T00:00:00`);
  const targetDate = new Date(`${dateIso}T00:00:00`);
  const dayOffset = Math.round(
    (targetDate.getTime() - weekStart.getTime()) / (1000 * 60 * 60 * 24),
  );
  return week.discoveries.find((d) => d.rabbit_hole_day_index === dayOffset);
}
